import {
  CLEAR_REWARDS_SUCCESS,
  RECEIVE_APPLY_REDEEMABLE_REWARDS,
  RECEIVE_APPLY_REDEEMABLE_REWARDS_ERROR,
  RECEIVE_REDEMPTION_INCREMENTS,
  RECEIVE_REWARDS_ERROR,
  RECEIVE_REWARDS_INFO,
  RECEIVE_REWARDS_TERMS,
  RECEIVE_SIGNUP_FOR_REWARDS,
  REQUEST_APPLY_REDEEMABLE_REWARDS,
  REQUEST_REDEMPTION_INCREMENTS,
  REQUEST_REWARDS_INFO,
  REQUEST_SIGNUP_FOR_REWARDS,
  RR_CHANGE_REDEEM_AMOUNT,
  RR_COMPONENT_VIEW
} from 'constants/reduxActions';
import { HF_TOPBANNER_CONTENT_TYPE_REWARDS } from 'constants/rewardsInfo';

import { authenticationErrorCatchHandlerFactory } from 'actions/errors';
import { setSessionCookies } from 'actions/session';
import { setHFTopBannerContent, showHFTopBanner } from 'actions/headerfooter';
import {
  enrollRewards,
  getRedeemableRewards,
  getRewards,
  getRewardsTerms,
  getRewardsTransactionStatus,
  postRedeemableRewards
} from 'apis/mafia';
import { fetchApiNotAuthenticatedMiddleware, fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { prependAppRoot } from 'history/AppRootUtils';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { trackError } from 'helpers/ErrorUtils';
import { storeRewardsInfo } from 'helpers/MyAccountUtils';

export function requestSignupForRewards() {
  return {
    type: REQUEST_SIGNUP_FOR_REWARDS
  };
}

export function receiveSignupForRewards(resp) {
  return {
    type: RECEIVE_SIGNUP_FOR_REWARDS,
    enrolledSince: resp.unix_timestamp
  };
}

export function requestRewardsInfo() {
  return {
    type: REQUEST_REWARDS_INFO
  };
}

export function receiveRewardsInfo(rewardsInfo) {
  return {
    type: RECEIVE_REWARDS_INFO,
    rewardsInfo: rewardsInfo.data
  };
}

export function receiveRewardsTerms(resp) {
  const { slotData } = resp;
  return {
    type: RECEIVE_REWARDS_TERMS,
    terms: slotData['primary-1']
  };
}

export function receiveRewardsError() {
  return {
    type: RECEIVE_REWARDS_ERROR
  };
}

export function requestRedemptionIncrements() {
  return {
    type: REQUEST_REDEMPTION_INCREMENTS
  };
}

export function receiveRedemptionIncrements(increments) {
  return {
    type: RECEIVE_REDEMPTION_INCREMENTS,
    increments
  };
}

export function requestApplyRedeemableRewards() {
  return {
    type: REQUEST_APPLY_REDEEMABLE_REWARDS
  };
}

export function receiveApplyRedeemableRewards(redemptionSuccessInfo) {
  return {
    type: RECEIVE_APPLY_REDEEMABLE_REWARDS,
    redemptionSuccessInfo
  };
}

export function receiveApplyRedeemableRewardsError() {
  return {
    type: RECEIVE_APPLY_REDEEMABLE_REWARDS_ERROR
  };
}

export function clearRewardsSuccess() {
  return {
    type: CLEAR_REWARDS_SUCCESS
  };
}

export function onChangeRedemptionAmountEvent() {
  return {
    type: RR_CHANGE_REDEEM_AMOUNT
  };
}

export function onRedeemRewardsComponentView() {
  return {
    type: RR_COMPONENT_VIEW
  };
}

export function signupForRewards(returnTo) {
  return (dispatch, getState) => {
    dispatch(requestSignupForRewards());
    const { cookies, environmentConfig: { api: { mafia }, akitaKey }, routing: { locationBeforeTransitions } } = getState();
    return enrollRewards(mafia, akitaKey, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        dispatch(receiveSignupForRewards(resp));
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(returnTo || '/rewards/dashboard', locationBeforeTransitions)));
  };
}

export function fetchRewardsInfo(options = {}, getRewardsApi = getRewards) {
  const { returnTo , updateTopBannerData } = options;
  return (dispatch, getState) => {
    dispatch(requestRewardsInfo());
    const { cookies, environmentConfig: { api: { mafia }, akitaKey }, routing: { locationBeforeTransitions } } = getState();
    return getRewardsApi(mafia, akitaKey, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { data, data: { consented } } = resp;
        if (consented) {
          dispatch(receiveRewardsInfo(resp));
        }
        dispatch(showHFTopBanner());

        if (updateTopBannerData) {
          const { tier, points_to_next_tier: pointsToNextTier, spend_points: spendPoints, benefits } = data;
          const bannerContent = { tier, pointsToNextTier, spendPoints, benefits };
          dispatch(setHFTopBannerContent({ bannerType: HF_TOPBANNER_CONTENT_TYPE_REWARDS, bannerContent }));
          storeRewardsInfo(bannerContent, cookies['session-id']);
        }

        if (!consented) {
          return getRewardsTerms(mafia)
            .then(fetchErrorMiddleware)
            .then(pageContent => {
              dispatch(receiveRewardsTerms(pageContent));
              dispatch(receiveRewardsInfo(resp));
            });
        }
      })
      .catch(e => {
        if (returnTo) { // given returnTo on rewards/dashboard page, check for auth and redirect back to dashboard if not logged in
          return authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(returnTo, locationBeforeTransitions))(e);
        } else { // otherwise, just fail silently (eg. on main /account page)
          dispatch(receiveRewardsError());
          trackError('NON-FATAL', 'Could not load rewards data.', e);
        }
      });
  };
}

export function fetchRedeemableRewards(getRedeemableRewardsApi = getRedeemableRewards) {
  return (dispatch, getState) => {
    dispatch(requestRedemptionIncrements());
    const { cookies, environmentConfig: { api: { mafia }, akitaKey } } = getState();
    return getRedeemableRewardsApi(mafia, akitaKey, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ data }) => {
        dispatch(receiveRedemptionIncrements(data['redemption_increments']));
      })
      .catch(e => {
        dispatch(receiveRewardsError());
        trackError('NON-FATAL', 'Could not load redeemable rewards data.', e);
      });
  };
}

export function applyRedeemableRewards(spendPoints, postRedeemableRewardsApi = postRedeemableRewards) {
  return (dispatch, getState) => {
    dispatch(requestApplyRedeemableRewards());
    const { cookies, environmentConfig: { api: { mafia }, akitaKey } } = getState();
    return postRedeemableRewardsApi(mafia, spendPoints, akitaKey, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        if (resp) {
          const transactionId = resp['transaction_id'];
          const callCount = 1;
          dispatch(fetchRewardsTransactionStatus(transactionId, callCount));
        }
      })
      .catch(e => {
        dispatch(receiveApplyRedeemableRewardsError());
        trackError('NON-FATAL', 'Could not redeem rewards.', e);
      });
  };
}

export function fetchRewardsTransactionStatus(transactionId, callCount, getRewardsTransactionStatusApi = getRewardsTransactionStatus) {
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia }, akitaKey } } = getState();
    return getRewardsTransactionStatusApi(mafia, transactionId, akitaKey, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ data }) => {
        if (callCount > 3) {
          return false; // @todo implement error if max retries reached
        }
        callCount++;

        if (data.length && data[0].status === 'skip') {
          dispatch(receiveApplyRedeemableRewards(data[0]));
          dispatch(fetchRewardsInfo()); // refresh rewards info
        } else { // retry after 3 seconds
          return setTimeout(() => {
            dispatch(fetchRewardsTransactionStatus(transactionId));
          }, 3000);
        }
      })
      .catch(e => {
        dispatch(receiveRewardsError());
        trackError('NON-FATAL', 'Could not get rewards transaction status.', e);
      });
  };
}
